<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import {
  chartColor,
  chartTime,
  panRange,
  useChartJs,
  zoomRange,
  type ChartRange
} from '@/shared/lib'
import { Button } from '@/shared/ui/Button'
import { Hint } from '@/shared/ui/Hint'

import type { RealtimeChartProps } from './types'

/**
 * The Chart.js drawing of `RealtimeChart`. Drag across it to zoom into that stretch, or
 * Ctrl/⌘ + wheel (a pinch on a trackpad) to zoom around the cursor; a horizontal scroll or
 * Shift + wheel moves the zoomed window, and "Reset zoom" shows the whole range. A plain
 * wheel is left to the page, so scrolling past the chart never zooms it. The zoom survives
 * both a metric switch and fresh data.
 */
const props = withDefaults(defineProps<RealtimeChartProps>(), {
  start: 0,
  height: 500
})

/** A drag shorter than this is a click, and zooms nothing. */
const MIN_DRAG_PX = 8

/** Tick steps an axis may take, in minutes; the smallest that keeps it under ~10 labels. */
const STEPS = [1, 5, 10, 15, 30, 60, 120, 180, 360, 720, 1440]
const MINUTE_MS = 60 * 1000

const MIN_ZOOM_MS = 10 * MINUTE_MS

/** How far one pixel of wheel travel zooms: ~10% per notch of a mouse wheel. */
const WHEEL_ZOOM_RATE = 0.001

/** A line of a line-mode wheel (Firefox with a mouse), in pixels. */
const WHEEL_LINE_PX = 16

const IS_MAC = /Mac|iPhone|iPad/.test(navigator.platform)

const formatTime = new Intl.DateTimeFormat(undefined, {
  hour: '2-digit',
  minute: '2-digit'
}).format
const formatDateTime = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short'
}).format

const points = computed(() => props.data[props.metric] ?? [])
const times = computed(() =>
  points.value.map((point) => chartTime(point.datetime))
)
const extent = computed<ChartRange>(() => ({
  min: Math.min(...times.value),
  max: Math.max(...times.value)
}))

/** The reader's zoom; `null` until they drag, and the axis then opens at `start`. */
const zoom = ref<ChartRange | null>(null)

const shown = computed<ChartRange>(() => {
  if (zoom.value) return zoom.value

  const { min, max } = extent.value

  return { min: min + (max - min) * props.start, max }
})
const isZoomed = computed(
  () => shown.value.min > extent.value.min || shown.value.max < extent.value.max
)

const { element, chart, render } = useChartJs({
  hasData: () => points.value.length > 0,
  draw: ({ Chart }, host) => {
    const line = (
      field: 'today' | 'yesterday',
      label: string,
      colour: string
    ) => ({
      label,
      data: points.value.map((point, index) => ({
        x: times.value[index] ?? 0,
        y: point[field]
      })),
      borderColor: colour,
      // `#rrggbb` plus an alpha byte: the area under a line at 30%.
      backgroundColor: `${colour}4d`,
      borderWidth: 1,
      fill: 'origin' as const,
      pointRadius: 0,
      // amCharts' `tensionX: 0.77`.
      tension: 0.23
    })

    return new Chart(host, {
      type: 'line',
      data: {
        datasets: [
          line('today', 'Today', chartColor('--el-color-primary')),
          line(
            'yesterday',
            'Yesterday',
            chartColor('--el-text-color-placeholder')
          )
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              title: (items) => formatDateTime(items[0]?.parsed.x ?? 0)
            }
          }
        },
        scales: {
          x: {
            type: 'linear',
            min: shown.value.min,
            max: shown.value.max,
            // A linear axis picks round numbers of milliseconds: step through whole
            // minutes instead, so every label is a time a reader would write down.
            afterBuildTicks: (axis) => {
              const span = (axis.max - axis.min) / MINUTE_MS
              const step =
                (STEPS.find((minutes) => span / minutes <= 10) ?? 1440) *
                MINUTE_MS
              // Multiples of the step count from the epoch, which is UTC: shift by the
              // local offset, or a three-hour step lands on 02:00, 05:00 east of it.
              const offset = new Date(axis.min).getTimezoneOffset() * MINUTE_MS
              const ticks = []

              for (
                let value =
                  Math.ceil((axis.min - offset) / step) * step + offset;
                value <= axis.max;
                value += step
              )
                ticks.push({ value })

              axis.ticks = ticks
            },
            ticks: { callback: (value) => formatTime(Number(value)) }
          },
          y: {
            // Counts never go below zero, and a flat zero day would otherwise get a
            // -1…1 axis.
            beginAtZero: true,
            title: { display: true, text: props.metric }
          }
        }
      }
    })
  }
})

watch(() => [props.metric, props.data], render, { flush: 'post' })

function applyZoom(range: ChartRange) {
  zoom.value = range

  const axis = chart.value?.options.scales?.x

  if (!chart.value || !axis) return

  axis.min = range.min
  axis.max = range.max
  chart.value.update('none')
}

/** The stretch being dragged over, in px from the canvas' left edge. */
const selection = ref<{ from: number; to: number } | null>(null)

function offsetX(event: MouseEvent): number {
  const box = (event.currentTarget as HTMLElement).getBoundingClientRect()

  return event.clientX - box.left
}

function onPointerDown(event: PointerEvent) {
  const area = chart.value?.chartArea
  const x = offsetX(event)

  if (!area || x < area.left || x > area.right) return
  ;(event.currentTarget as HTMLElement).setPointerCapture(event.pointerId)
  selection.value = { from: x, to: x }
}

function onPointerMove(event: PointerEvent) {
  const area = chart.value?.chartArea

  if (!selection.value || !area) return

  const x = Math.min(Math.max(offsetX(event), area.left), area.right)

  selection.value = { ...selection.value, to: x }
}

function onPointerUp() {
  const scale = chart.value?.scales.x
  const drag = selection.value

  selection.value = null

  if (!scale || !drag || Math.abs(drag.to - drag.from) < MIN_DRAG_PX) return

  applyZoom({
    min: scale.getValueForPixel(Math.min(drag.from, drag.to)) ?? scale.min,
    max: scale.getValueForPixel(Math.max(drag.from, drag.to)) ?? scale.max
  })
}

// Wheel input waiting for the next frame: a trackpad fires far more events than it draws.
let queued: ChartRange | null = null

function queueZoom(range: ChartRange) {
  if (!queued)
    requestAnimationFrame(() => {
      const next = queued

      queued = null
      if (next) applyZoom(next)
    })

  queued = range
}

function onWheel(event: WheelEvent) {
  const scale = chart.value?.scales.x
  const area = chart.value?.chartArea

  if (!scale || !area) return

  const unit = event.deltaMode === 1 ? WHEEL_LINE_PX : 1
  const current = queued ?? shown.value
  const zooming = event.ctrlKey || event.metaKey
  // Shift + wheel is a horizontal scroll everywhere, but not every browser swaps the axes.
  const across = event.deltaX || (event.shiftKey ? event.deltaY : 0)

  if (zooming) {
    const anchor = scale.getValueForPixel(offsetX(event)) ?? current.min
    const factor = Math.exp(event.deltaY * unit * WHEEL_ZOOM_RATE)

    queueZoom(zoomRange(current, extent.value, anchor, factor, MIN_ZOOM_MS))
  } else if (across && Math.abs(across) >= Math.abs(event.deltaY)) {
    // Taken even with nothing to move: a sideways scroll left to the page is the browser's
    // Back on macOS, and the reader loses the screen.
    if (isZoomed.value) {
      const perPixel = (current.max - current.min) / (area.right - area.left)

      queueZoom(panRange(current, extent.value, across * unit * perPixel))
    }
  } else {
    return
  }

  event.preventDefault()
}

function resetZoom() {
  applyZoom({ ...extent.value })
}

const selectionStyle = computed(() => {
  if (!selection.value) return null

  const { from, to } = selection.value

  return { left: `${Math.min(from, to)}px`, width: `${Math.abs(to - from)}px` }
})
</script>

<template>
  <div class="realtime-chart" :style="{ height: `${height}px` }">
    <div
      class="realtime-chart__plot"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="selection = null"
      @wheel="onWheel"
    >
      <canvas ref="element" />
      <div
        v-if="selectionStyle"
        class="realtime-chart__selection"
        :style="selectionStyle"
      />
    </div>
    <Button
      v-if="isZoomed"
      class="realtime-chart__reset"
      size="small"
      @click="resetZoom"
    >
      Reset zoom
    </Button>
    <Hint>
      Drag to zoom into a stretch · {{ IS_MAC ? '⌘' : 'Ctrl' }} + scroll to zoom
      · Shift + scroll to move
    </Hint>
  </div>
</template>

<style lang="scss" scoped>
.realtime-chart {
  position: relative;

  display: flex;
  flex-direction: column;

  &__plot {
    position: relative;
    flex: 1;
    min-height: 0;
    // Vertical swipes still scroll the page; a horizontal one is the zoom drag.
    touch-action: pan-y;
    user-select: none;
  }

  &__selection {
    position: absolute;
    top: 0;
    bottom: 0;
    pointer-events: none;
    background: var(--el-color-primary-light-8);
    opacity: 0.6;
  }

  &__reset {
    position: absolute;
    top: 0;
    right: 0;
  }
}
</style>
